import type { DetectedPreset } from '@/core/preset-selection'
import type { LayoutDetection } from '@/presets/types'
import { findArtaleHud, artaleHudRegions } from '@/presets/artale/layout-locator'
import { locateMxdcLayout } from '@/presets/mxdc/layout-locator'
import { findGcwHud, gcwHudRegions } from '@/presets/gcw/layout-locator'
import { findWorldsHud, worldsHudRegions } from '@/presets/worlds/layout-locator'
export interface UiProbeResult { kind: DetectedPreset | 'unknown' | 'ambiguous'; layout?: LayoutDetection }

/** Isolated fields never establish a game family. Both complete HUDs are ambiguous. */
export function probeGameUi(frame: ImageData): UiProbeResult {
  const deadlineAt = Date.now() + 5_000
  let artale: ReturnType<typeof findArtaleHud>, classic: LayoutDetection, gcw: ReturnType<typeof findGcwHud>
  try {
    artale = findArtaleHud(frame, { deadlineAt })
    classic = locateMxdcLayout(frame, ['hp', 'mp', 'exp', 'level', 'jobName'], { deadlineAt })
    gcw = findGcwHud(frame, { deadlineAt })
  } catch (error) {
    if (error instanceof DOMException && error.name === 'TimeoutError') return { kind: 'unknown' }
    throw error
  }
  const classicFound = ['hp', 'mp', 'exp'].filter(metric => classic.regions[metric as 'hp']).length >= 2
    && Boolean(classic.regions.level)
  // Keep the original searches and deadline intact. Worlds' distinct two-row
  // signature is checked after them, so it cannot starve an existing candidate.
  let worlds: ReturnType<typeof findWorldsHud> = []
  try { worlds = findWorldsHud(frame, { deadlineAt }) }
  catch (error) { if (!(error instanceof DOMException && error.name === 'TimeoutError')) throw error }
  if (worlds.length > 1 || worlds.length && (artale.length || gcw.length || classicFound || classic.diagnostics?.reason === 'multiple-scenes')) return { kind: 'ambiguous' }
  if (worlds[0]) return { kind: 'worlds', layout: worldsHudRegions(frame, worlds[0]) }
  if (artale.length > 1 || gcw.length > 1 || classic.diagnostics?.reason === 'multiple-scenes') return { kind: 'ambiguous' }
  if (gcw[0]) {
    const layout = gcwHudRegions(frame, gcw[0])
    // The classic compact matcher also recognizes this HUD. Merge only spatially
    // coincident matches; a separate classic window is still an ambiguity.
    const sameScene = (['hp', 'mp', 'exp'] as const).some(metric => {
      const a = layout.regions[metric]?.textRect, b = classic.regions[metric]?.textRect
      return a && b && Math.abs(a.x - b.x) * frame.width < 8 * gcw[0]!.sx
        && Math.abs(a.y - b.y) * frame.height < 8 * gcw[0]!.sy
    })
    if (artale.length || classicFound && !sameScene) return { kind: 'ambiguous' }
    return { kind: 'gcw', layout }
  }
  if (artale.length && classicFound) return { kind: 'ambiguous' }
  if (artale[0]) return { kind: 'artale', layout: artaleHudRegions(frame, artale[0]) }
  return classicFound ? { kind: 'mxdc', layout: classic } : { kind: 'unknown' }
}
